<template>
    <div>
        <Modal v-if="paddleSubscription" size="xl" @close="$emit('close')">
            <template #header>
                <div class="text-gray-600">
                    {{
                        subscriptionCanceled
                            ? $t("vue.resume_subscription")
                            : $t("vue.change_subscription_frequency")
                    }}
                </div>
            </template>

            <template #body>
                <div class="px-6 pb-6 pt-3">
                    <div
                        class="flex flex-col rounded bg-blue-100 px-[14px] pb-2.5 pt-3 sm:flex-row"
                        :class="{
                            'bg-blue-100': !subscriptionCanceled,
                            'bg-red-100': subscriptionCanceled,
                        }"
                    >
                        <div
                            :class="{
                                'text-blue-600': !subscriptionCanceled,
                                'text-custom-alert-100': subscriptionCanceled,
                            }"
                        >
                            <svg
                                width="20"
                                height="20"
                                viewBox="0 0 20 20"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path
                                    fill-rule="evenodd"
                                    clip-rule="evenodd"
                                    d="M18 10C18 12.1217 17.1571 14.1566 15.6569 15.6569C14.1566 17.1571 12.1217 18 10 18C7.87827 18 5.84344 17.1571 4.34315 15.6569C2.84285 14.1566 2 12.1217 2 10C2 7.87827 2.84285 5.84344 4.34315 4.34315C5.84344 2.84285 7.87827 2 10 2C12.1217 2 14.1566 2.84285 15.6569 4.34315C17.1571 5.84344 18 7.87827 18 10V10ZM11 6C11 6.26522 10.8946 6.51957 10.7071 6.70711C10.5196 6.89464 10.2652 7 10 7C9.73478 7 9.48043 6.89464 9.29289 6.70711C9.10536 6.51957 9 6.26522 9 6C9 5.73478 9.10536 5.48043 9.29289 5.29289C9.48043 5.10536 9.73478 5 10 5C10.2652 5 10.5196 5.10536 10.7071 5.29289C10.8946 5.48043 11 5.73478 11 6ZM9 9C8.73478 9 8.48043 9.10536 8.29289 9.29289C8.10536 9.48043 8 9.73478 8 10C8 10.2652 8.10536 10.5196 8.29289 10.7071C8.48043 10.8946 8.73478 11 9 11V14C9 14.2652 9.10536 14.5196 9.29289 14.7071C9.48043 14.8946 9.73478 15 10 15H11C11.2652 15 11.5196 14.8946 11.7071 14.7071C11.8946 14.5196 12 14.2652 12 14C12 13.7348 11.8946 13.4804 11.7071 13.2929C11.5196 13.1054 11.2652 13 11 13V10C11 9.73478 10.8946 9.48043 10.7071 9.29289C10.5196 9.10536 10.2652 9 10 9H9Z"
                                    fill="currentColor"
                                />
                            </svg>
                        </div>
                        <div
                            class="mt-2 text-sm sm:ml-[14px] sm:mt-0"
                            :class="{
                                'text-blue-600': !subscriptionCanceled,
                                'text-red-600': subscriptionCanceled,
                            }"
                        >
                            {{ $t("vue.you_are_currently_using") }}
                            <span
                                :class="{
                                    'text-blue-700': !subscriptionCanceled,
                                    'text-red-700': subscriptionCanceled,
                                }"
                            >
                                {{
                                    paddleSubscription.period === "month"
                                        ? $t("vue.month_to_month")
                                        : $t("vue.annual")
                                }}
                                {{ $t("vue.subscription_plan") }}
                                <span v-if="subscriptionCanceled">{{
                                    $t("vue.your_subscription_is_canceled")
                                }}</span>
                            </span>
                        </div>
                    </div>
                    <div
                        class="mt-5 rounded-md border border-gray-200 bg-gray-50 px-6 pb-6 pt-4"
                    >
                        <SelectInput
                            v-if="!subscriptionCanceled"
                            ref="selectInput"
                            v-model="period"
                            :data="plans"
                            :label="$t('vue.choose_subscription_period')"
                            :placeholder="
                                $t('vue.choose_subscription_period_placeholder')
                            "
                            class="max-w-[400px]"
                        />

                        <div class="mt-3">
                            <div
                                class="text-2xl leading-normal tracking-tighter text-gray-800"
                            >
                                <span class="font-bold">
                                    ${{
                                        selectedPlan.totalPrice * domainsCount
                                    }}
                                </span>
                                <span
                                    >/{{
                                        period === "monthly"
                                            ? $t("vue.month")
                                            : $t("vue.year")
                                    }}
                                </span>
                            </div>

                            <div class="text-sm text-gray-500">
                                {{ $t("vue.for") }}
                                {{ domainsCount }}
                                {{
                                    domainsCount === 1
                                        ? $t("vue.domain")
                                        : $t("vue.domains")
                                }}
                            </div>

                            <div
                                v-if="
                                    selectedPlan.priceId ===
                                        paddleSubscription.items[0].id &&
                                    paddleSubscription.next_billing_at
                                "
                                class="mt-[18px] text-sm"
                            >
                                <div class="font-medium text-gray-800">
                                    {{ $t("vue.next_billing_date") }}
                                    {{
                                        format(
                                            new Date(
                                                paddleSubscription
                                                    .next_billing_at.date,
                                            ),
                                            "d MMMM, yyyy",
                                        )
                                    }}
                                </div>
                            </div>

                            <div
                                v-else-if="
                                    selectedPlan.priceId !==
                                    paddleSubscription.items[0].id
                                "
                                class="mt-[18px] text-sm"
                            >
                                <div class="font-medium text-gray-800">
                                    {{
                                        $t(
                                            "vue.you_will_be_charged_on_next_billing_date",
                                        )
                                    }}
                                    {{
                                        format(
                                            new Date(
                                                period === "monthly"
                                                    ? addMonths(new Date(), 1)
                                                    : addYears(new Date(), 1),
                                            ),
                                            "d MMMM, yyyy",
                                        )
                                    }}
                                </div>
                            </div>

                            <div
                                v-else-if="subscriptionCanceled"
                                class="mt-[18px] text-sm text-gray-500"
                            >
                                <div>
                                    {{
                                        $t(
                                            "vue.subscription_canceled_will_be_active_until",
                                        )
                                    }}
                                </div>
                                <div class="font-medium text-gray-800">
                                    {{
                                        format(
                                            new Date(
                                                paddleSubscription.canceled_at
                                                    ? paddleSubscription
                                                          .canceled_at.date
                                                    : paddleSubscription.change
                                                          ?.date.date,
                                            ),
                                            "d MMMM, yyyy",
                                        )
                                    }}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="mt-5">
                        <CheckboxInput
                            v-model:checked="confirmation"
                            size="md"
                            name="confirm"
                            :label="$t('vue.confirm_change_subscription')"
                        />
                    </div>
                </div>
            </template>

            <template #footer>
                <div class="flex justify-between border-t border-gray-200 p-6">
                    <AtomicButton
                        color="white"
                        size="md"
                        @click="$emit('close')"
                    >
                        {{ $t("vue.cancel") }}
                    </AtomicButton>

                    <AtomicButton
                        :disabled="loading || !confirmation"
                        size="md"
                        color="yellow"
                        @click="
                            subscriptionCanceled
                                ? resumeSubscription()
                                : changeSubscriptionFrequency()
                        "
                    >
                        <span>
                            {{
                                subscriptionCanceled
                                    ? $t("vue.resume_subscription")
                                    : $t("vue.change_frequency")
                            }}
                        </span>
                    </AtomicButton>
                </div>
            </template>
        </Modal>
    </div>
</template>
<script lang="ts" setup>
import { format, addMonths, addYears } from "date-fns";
import { computed, ref } from "vue";
import route from "ziggy-js";
import { useTranslations } from "@/composables/useTranslations";

import { router } from "@inertiajs/vue3";
import { create } from "@/composables/useNotifications";

const emit = defineEmits(["close"]);

// Get access to translations
const { t } = useTranslations();

const props = defineProps({
    domainsCount: {
        type: Number,
        default: () => 0,
    },
    paddlePriceData: {
        type: Array as () => {
            type: "monthly" | "annually";
            priceId: string;
            totalPrice?: number;
        }[],
        default: () => [],
    },
    paddleEnvironment: {
        type: String as () => "production" | "sandbox",
        default: "production",
    },
    paddleSubscription: {
        type: Object as PropType<IPaddleSubscription | null>,
        default: null,
    },
    paddleCustomerPortalUrl: {
        type: String,
        default: "",
    },
});

const plans = [
    {
        title: "Month-to-month",
        value: "monthly",
    },
    {
        title: "Annual",
        value: "annually",
    },
];

const subscriptionCanceled = computed(() => {
    return (
        props.paddleSubscription?.status === "canceled" ||
        props.paddleSubscription?.change?.action === "cancel"
    );
});

const confirmation = ref(false);
const loading = ref(false);
const period = ref(
    props.paddleSubscription?.period === "month" ? "monthly" : "annually",
);

const selectedPlan = computed(() => {
    return props.paddlePriceData.find((plan) => plan.type === period.value);
});

const changeSubscriptionFrequency = async () => {
    loading.value = true;
    router.put(
        route("billing.subscriptions.frequency.update"),
        {
            period: period.value,
        },
        {
            preserveScroll: true,
            onSuccess: () => {
                create({
                    title: t("vue.subscription_changed"),
                    text: t("vue.subscription_changed_successfully"),
                    type: "success",
                });
                emit("close");
            },
            onError: () => {
                create({
                    title: t("vue.subscription_not_changed"),
                    text: t("vue.error_changing_subscription"),
                    type: "error",
                });
            },
            onFinish: () => {
                loading.value = false;
            },
        },
    );
};

const resumeSubscription = () => {
    loading.value = true;

    router.post(
        route("billing.subscriptions.resume"),
        {},
        {
            preserveScroll: true,
            onSuccess: () => {
                create({
                    title: t("vue.subscription_resumed"),
                    text: t("vue.subscription_resumed_successfully"),
                    type: "success",
                });
                emit("close");
            },
            onError: () => {
                create({
                    title: t("vue.subscription_not_resumed"),
                    text: t("vue.error_resuming_subscription"),
                    type: "error",
                });
            },
            onFinish: () => {
                loading.value = false;
            },
        },
    );
};
</script>
